/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import validate from 'validate.js';
import { Grid } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { getVeiculosOptions } from '../../../store/veiculo/reducer';
import { getMalhasOptions } from '../../../store/malha/reducer';
import {
  DialogDefaultV2,
  InputV2,
  MalhaSelectV2,
  NsirSelectV2,
  OptionSelectV2,
  VeiculoSelectV2,
} from '../../../components/v2';
import {
  TIPO_PROGRAMACAO_DIARIA_DESLOCAMENTO,
  TIPO_PROGRAMACAO_DIARIA_INSPECAO,
  TIPO_PROGRAMACAO_DIARIA_MANUTENCAO_VEICULOS,
} from '../../../constants/ids';
import { buildRequiredSchema } from 'utils';

const isInspecao = tipoId => TIPO_PROGRAMACAO_DIARIA_INSPECAO === tipoId;
const isDeslocamento = tipoId => TIPO_PROGRAMACAO_DIARIA_DESLOCAMENTO === tipoId;
const showMalha = tipoId => tipoId && !isInspecao(tipoId);
const showCidade = tipoId => tipoId && !isInspecao(tipoId) && !isDeslocamento(tipoId);

const getItemSchema = data => {
  const schema = buildRequiredSchema(['tipoId']);
  if (isDeslocamento(data.tipoId)) {
    return { ...schema, ...buildRequiredSchema(['cidadeDe', 'cidadePara']) };
  }
  if (isInspecao(data.tipoId)) {
    return { ...schema, ...buildRequiredSchema(['nsirIds', 'descricao']) };
  }
  if (TIPO_PROGRAMACAO_DIARIA_MANUTENCAO_VEICULOS === data.tipoId) {
    return { ...schema, ...buildRequiredSchema(['veiculoId']) };
  }
  return schema;
};

const emptyItem = () => ({});

const ItemFormulario = ({ open, selectedItem, onClose, onSave, veiculosOptions, malhasOptions }) => {
  const [values, setValues] = useState(emptyItem());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setValues(selectedItem ? { ...selectedItem } : emptyItem());
      setErrors({});
    }
  }, [open, selectedItem]);

  const handleClose = () => {
    setErrors({});
    setValues(emptyItem());
    onClose();
  };

  const handleChange = event => setValues({ ...values, [event.target.name]: event.target.value });

  const handleTipoSelect = (tipoId, tipoNome) => {
    setValues({
      ...values,
      tipoId,
      tipo: tipoNome || values.tipo,
      cidadeDe: undefined,
      cidadePara: undefined,
      cidade: undefined,
      malhaId: undefined,
      malha: undefined,
      nsirIds: undefined,
      nsirCodes: undefined,
      veiculoId: undefined,
      veiculo: undefined,
    });
  };

  const handleMalhaSelect = malhaId => {
    const option = (malhasOptions || []).find(m => m.id === malhaId);
    setValues({
      ...values,
      malhaId: malhaId || undefined,
      malha: option?.nome || '',
    });
  };

  const handleVeiculoSelect = veiculoId => {
    const option = (veiculosOptions || []).find(v => v.id === veiculoId);
    setValues({
      ...values,
      veiculoId,
      veiculo: option?.placa || '',
    });
  };

  const handleConfirm = () => {
    const erros = validate(values, getItemSchema(values));
    setErrors(erros || {});
    if (erros) return;

    const payload = { ...values, id: values.id || uuidv4() };

    if (!showMalha(payload.tipoId)) {
      payload.malhaId = undefined;
      payload.malha = undefined;
    }
    if (!showCidade(payload.tipoId)) {
      payload.cidade = undefined;
    }
    if (!isDeslocamento(payload.tipoId)) {
      payload.cidadeDe = undefined;
      payload.cidadePara = undefined;
    }

    onSave(payload);
    handleClose();
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={handleClose}
      handlerConfirm={handleConfirm}
      title={selectedItem ? 'Editar item' : 'Novo item'}
      confirmMessage="Adicionar"
      maxWidth="md"
      fullWidth
    >
      <Grid container spacing={1} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <OptionSelectV2
            label="Tipo da Programação"
            codigo="TIPO_PROGRAMACAO_DIARIA"
            isRequired
            onSelect={handleTipoSelect}
            value={values.tipoId}
            error={!!(errors && errors.tipoId)}
            helperText={errors && errors.tipoId ? errors.tipoId[0] || errors.tipoId : null}
          />
        </Grid>

        {showMalha(values.tipoId) && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <MalhaSelectV2
              onSelect={handleMalhaSelect}
              value={values.malhaId}
            />
          </Grid>
        )}

        {showCidade(values.tipoId) && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputV2
              label="Cidade"
              name="cidade"
              onChange={handleChange}
              value={values.cidade || ''}
            />
          </Grid>
        )}

        {isDeslocamento(values.tipoId) && (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputV2
                error={!!(errors && errors.cidadeDe)}
                helperText={errors && errors.cidadeDe ? errors.cidadeDe[0] || errors.cidadeDe : null}
                label="Cidade De"
                name="cidadeDe"
                onChange={handleChange}
                value={values.cidadeDe || ''}
                isRequired
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputV2
                error={!!(errors && errors.cidadePara)}
                helperText={errors && errors.cidadePara ? errors.cidadePara[0] || errors.cidadePara : null}
                label="Cidade Para"
                name="cidadePara"
                onChange={handleChange}
                value={values.cidadePara || ''}
                isRequired
              />
            </Grid>
          </>
        )}

        {isInspecao(values.tipoId) && (
          <Grid size={{ xs: 12 }}>
            <NsirSelectV2
              onSelect={(nsirIds, nsirCodes) => setValues({
                ...values,
                nsirIds,
                nsirCodes: nsirCodes || [],
              })}
              value={values.nsirIds}
              isRequired
              error={!!(errors && errors.nsirIds)}
              helperText={errors && errors.nsirIds ? errors.nsirIds[0] || errors.nsirIds : null}
            />
          </Grid>
        )}

        {TIPO_PROGRAMACAO_DIARIA_MANUTENCAO_VEICULOS === values.tipoId && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <VeiculoSelectV2
              onSelect={handleVeiculoSelect}
              value={values.veiculoId}
              isRequired
              error={!!(errors && errors.veiculoId)}
              helperText={errors && errors.veiculoId ? errors.veiculoId[0] || errors.veiculoId : null}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <InputV2
            error={!!(errors && errors.descricao)}
            helperText={errors && errors.descricao ? errors.descricao[0] || errors.descricao : null}
            isRequired={isInspecao(values.tipoId)}
            label="Descrição"
            name="descricao"
            onChange={handleChange}
            value={values.descricao || ''}
            rows={3}
            multiline
          />
        </Grid>
      </Grid>
    </DialogDefaultV2>
  );
};

ItemFormulario.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedItem: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  veiculosOptions: PropTypes.array,
  malhasOptions: PropTypes.array,
};

ItemFormulario.defaultProps = {
  selectedItem: null,
  veiculosOptions: [],
  malhasOptions: [],
};

const mapStateToProps = state => ({
  veiculosOptions: getVeiculosOptions(state),
  malhasOptions: getMalhasOptions(state),
});

export default connect(mapStateToProps)(ItemFormulario);
